import React, { useState, useCallback } from 'react';
import { GOOGLE_CLIENT_ID, DRIVE_SCOPE, ALLOWED_EMAIL } from '../config.jsx';

// =============================================
// AUTH SCREEN COMPONENT
// =============================================
export function AuthScreen({ onSignedIn }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleGoogleLogin = useCallback(() => {
        setError('');
        setLoading(true);

        const client = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: DRIVE_SCOPE + ' email profile',
            callback: async (tokenResponse) => {
                if (tokenResponse.error) {
                    setError('Sign-in failed: ' + tokenResponse.error);
                    setLoading(false);
                    return;
                }

                // Fetch user info to verify email
                try {
                    const infoRes = await fetch(
                        'https://www.googleapis.com/oauth2/v3/userinfo',
                        { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
                    );
                    const userInfo = await infoRes.json();

                    if (userInfo.email !== ALLOWED_EMAIL) {
                        setError(`Access denied. This app is private.\n\nOnly the authorised account can sign in.`);
                        setLoading(false);
                        return;
                    }

                    window.__gAccessToken = tokenResponse.access_token;
                    // Store token expiry (tokens last 1 hour)
                    window.__gTokenExpiry = Date.now() + (tokenResponse.expires_in * 1000);

                    const sessionUser = {
                        email: userInfo.email,
                        name: userInfo.name,
                        picture: userInfo.picture,
                    };
                    localStorage.setItem('iPlanner_user', JSON.stringify(sessionUser));
                    onSignedIn(sessionUser, tokenResponse.access_token);
                } catch (err) {
                    setError('Failed to verify account: ' + err.message);
                    setLoading(false);
                }
            },
        });
        client.requestAccessToken();
        setLoading(false);
    }, [onSignedIn]);

    return (
        <div className="auth-screen">
            <div className="auth-logo">💎</div>
            <div className="auth-app-name">iPlanner</div>
            <div className="auth-tagline">Your Personal Financial Hub</div>

            <div className="auth-card">
                <div className="auth-title">Welcome Back</div>
                <div className="auth-subtitle">
                    Sign in with Google to access your financial data, synced securely across all your devices.
                </div>

                <button
                    className="google-signin-btn"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                >
                    <svg className="google-icon" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {loading ? 'Signing in…' : 'Continue with Google'}
                </button>

                {error && (
                    <div className="auth-error">⛔ {error}</div>
                )}

                <div className="auth-note">
                    🔒 This app is private. Only the authorised account can access it.<br />
                    Your data is stored in your own Google Drive — never on our servers.
                </div>
            </div>
        </div>
    );
}

// =============================================
// SYNC STATUS INDICATOR (for TopBar)
// =============================================
export function SyncIndicator({ syncStatus, lastSynced, onSyncNow, isOnline, syncStuck, onForceReconnect }) {
    const statusMap = {
        synced: { dot: 'synced', label: 'Synced' },
        syncing: { dot: 'syncing', label: 'Syncing…' },
        pulling: { dot: 'syncing', label: 'Updating…' }, // SYNC-011: inbound pull indicator
        connecting: { dot: 'syncing', label: 'Connecting…' },
        error: { dot: 'error', label: 'Sync error' },
        offline: { dot: 'offline', label: 'Offline' },
        idle: { dot: 'synced', label: 'Drive' },
    };
    const s = statusMap[syncStatus] || statusMap.idle;
    const tip = lastSynced ? `Last synced: ${lastSynced}` : 'Click to sync with Drive';

    // SYNC-FIX: Show reconnect button when sync is stuck
    if (syncStuck && isOnline) {
        return (
            <span className="sync-indicator sync-stuck" onClick={onForceReconnect} title="Sync stuck - click to reconnect">
                <span className="sync-dot error"></span>
                <span className="sync-label">Reconnect</span>
            </span>
        );
    }

    return (
        <span className="sync-indicator" onClick={onSyncNow} title={tip}>
            <span className={`sync-dot ${s.dot}`}></span>
            <span className="sync-label">{s.label}</span>
        </span>
    );
}
